<template>
    <main role="main" class="container text-center">
    <h1 v-if="mode == 'new'">Create Job</h1>
    <h1 v-else-if="mode == 'edit'">Job Details</h1>
    <notices :errors="errors" :notices="notices"></notices>
    <div class="row my-md-3 pb-md-5">
        <form class="form-inline col mr-md-3">
            <div class="form-group col">
                <label for="url" class="sr-only">URL</label>
                <input type="text" :disabled=isUrlInputDisabled v-b-popover.hover.top="'Copy/paste full URL you would like to track. Note that URL has to be publicly available (not requiring login)'" class="col form-control" v-model="url" id="url" aria-describedby="url" placeholder="https://">
                <b-button type="button" class="ml-md-3" v-on:click="clickRender()" variant="primary" :disabled="renderInProgress">
                    <b-spinner v-if="renderInProgress" style="width: 1.5rem; height: 1.5rem;" small label="Loading..."></b-spinner>
                    Go
                </b-button>
            </div>
        </form>
    </div>
    <div class="row py-md-3" v-if="mode == 'edit'">
        <h3>Job Run History</h3>
    </div>
    <jobRuns class="history-scroll" v-if="mode == 'edit'" @error="onError" :scheduleId="scheduleId"></jobRuns>
    <div class="row py-md-3" v-if="mode == 'edit'">
        <h3>Configure Job</h3>
    </div>
    <div class="row my-md-3" v-if="rules.length > 0 || mode == 'edit'">
        <form class="col bg-light mr-md-3 py-md-3 border">
            <div class="form-row">
                <div class="form-group col">
                    <label for="form-name" style="text-align: left">Job Name</label>
                    <input v-model="schedule.name" id="form-url" class="form-control">
                </div>
            </div>
            <div class="form-row px-md-5"> 
                <div class="form-group col-md-6">
                    <label for="form-url" style="text-align: left">Schedule Interval</label>
                    <select v-model="schedule.interval" id="form-interval" class="form-control">
                        <option value="FIVE_MIN">Every 5 Minutes</option>
                        <option value="FIFTEEN_MIN">Every 15 Minutes</option>
                        <option value="HALF_HOUR">Every Half Hour</option>
                        <option value="ONE_HOUR">Every Hour</option>
                        <option value="THREE_HOURS">Every 3 Hours</option>
                        <option value="SIX_HOURS">Every 6 Hours</option>
                        <option value="TWELVE_HOURS">Every 12 Hours</option>
                        <option value="ONE_DAY">Once a Day</option>
                        <option value="ONE_WEEK">Once a Week</option>
                        <option value="ONE_MONTH">Once a Month</option>
                    </select>
                </div>
                <div v-if="showHourMinutePicker(schedule.interval)" class="form-group col-md-4">
                    <label for="form-url" style="text-align: left">Start Hour/Minute</label>
                    <vue-timepicker v-model="timePicker" format="HH:mm" @input="timePickerInputHandler">></vue-timepicker>
                </div>
                <div v-if="showMinutePicker(schedule.interval)" class="form-group col-md-4">
                    <label for="form-url" style="text-align: left">Start Minute</label>
                    <vue-timepicker v-model="timePicker" format="mm" @input="timePickerInputHandler">></vue-timepicker>
                </div>
            </div>
            <div class="form-row">
                <div class="form-group col">
                    <label for="form-condition" style="text-align: left">Predicate Expression</label>
                    <b-button v-b-modal.expression-help variant="link">???</b-button>
                    <input v-model="schedule.condition" id="form-condition" class="form-control" placeholder='example: not match_prev RuleName' v-b-popover.hover.top="">
                </div>
            </div>
        </form>
        <div class="col bg-light py-md-3 border">
            <table class="table">
                <thead class="thead-light">
                    <tr>
                        <th>Fragment Name</th>
                        <th>Content Type</th>
                        <th>Action</th>
                    </tr>
                </thead>
                <tr v-bind:key="rule" v-for="(rule, idx) of rules">
                    <td>{{rule.name}}</td>
                    <td>{{rule.valueType}}</td>
                    <td>
                        <button @click="removeRule(idx)">remove</button>
                    </td>
                </tr>
            </table>
        </div>
    </div>
    <div v-else-if="checkRenderResp && !renderInProgress">Click on an element on the page to create a fragment</div>
    <div class="row my-md-3" v-if="checkRenderResp && !renderInProgress">
        <iframe class="col-12 bg-light my-md-3 py-md-3 border" src="javascript:void(0);" id="iframe-render"></iframe>
    </div>
    <div class="text-right col-12 py-md-3">
        <button v-if="mode == 'new' && rules.length > 0" @click="saveScheduleFinal" class="btn btn-primary">Create New Job</button>
        <button v-else-if="mode == 'edit' & rules.length > 0" @click="updateScheduleFinal" class="btn btn-primary">Update Job</button>
    </div>
    <b-modal id="modal-new-rule" title="New Fragment" ok-title="Add" @ok="addRule">
        <form class="form-inline" ref="form_add_rule">
            <div class="form-group mb-2">
                <label for="rule-name" class="sr-only">Name</label>
                <input type="text" name="rule-name" v-model="newRule.name" class="form-control" id="form-rule-name" placeholder="Name">
            </div>
            <div class="form-group mb-2">
                <select type="text" name="content-type" v-model="newRule.valueType" class="form-control" id="form-rule-content-type">
                    <option value="parseText" selected>Text</option>
                    <option value="parseNumber">Number</option>
                </select>
                <input type="hidden" name="selector" v-model="newRule.domQuery" id="form-rule-selector">
            </div>
        </form>
    </b-modal>
    <b-modal id="expression-help" title="Predicate Expression Help" size="lg" hide-footer>
        <p class="my-6">Use predicate expression to indicate the rule to succeed only if expression evaluates as true.</p>
        <p>
            For example. Let's assume you would like to be notified only if fragment's content has changed from previous run.
            Assuming fragment's name is "title", to accomplish our goal, you can use following predicate expression: <code>not match_prev title</code>
        </p>
        <p>
            How about checking if fragment contains certain text? easy, try this: <code>title contains "recipe"</code>. 
            And to negate you could do: <code>not title contains "recipe"</code></p>

        <p>
            Predicate expression can also work on numeric values. First ensure the fragment is of type `parseNumber`. 
            Assuming we have a fragment named "price" we can enforce following check: <code>price >= 10.0</code>
        </p>

        <p>Complete syntax:</p>
        <p>
            <code>[not] FRAGMENT_NAME match | contains "TEXT"</code><br />
            example: <code>title contains "recipe"</code>
        </p>
        <p>
            <code>[not] FRAGMENT_NAME == | &gt; | &gt;= | &lt; | &lt;= NUMBER</code><br />
            example: <code>price >= 10.0</code>
        </p>
        <p>
            <code>[not] match_prev FRAGMENT_NAME</code><br />
            example: <code>not match_prev title</code>
        </p>
        <p>
            <code>(expression) or | and (expression)</code><br />
            example: <code>(not match_prev title) and (price >= 10.0)</code>
        </p>
    </b-modal>
  </main>
</template>

<script>
import axios from 'axios';
import jQuery from 'jquery';
import slugify from 'slugify';
import _ from 'lodash';
import VueTimepicker from 'vue2-timepicker';
import Notices from '../components/Notices.vue';
import JobRuns from '../components/JobRuns.vue';
import CommonMixin from '../util/common';
import AppConfig from '../util/config';

export default {
    components: { VueTimepicker, Notices, JobRuns },
    name: 'NewJob',
    mixins: [CommonMixin],
    data() {
        return {
            mode: "new",
            scheduleId: null,
            url: "",
            submitRenderResp: null,
            polling: null,
            checkRenderResp: null,
            renderInProgress: false,
            rules: [],
            selectedRule: {}, // delete this var
            pollCount: 0,
            newRule: {"domQuery": "", "name": "", "valueType": "parseText"},
            schedule: {"interval": "", "name": "", "condition": ""},
            timePicker: {
                HH: "",
                mm: ""
            }
        }
    },
    computed: {
        isUrlInputDisabled() {
            if (this.mode == "new") {
                return false;
            } else {
                return true;
            }
        },
    },
    methods: {
        timePickerInputHandler(data) {
            this.timePicker.HH = data.HH;
            this.timePicker.mm = data.mm;
        },
        showHourMinutePicker(interval) {
            if (interval == "THREE_HOURS") return true;
            else if (interval == "SIX_HOURS") return true;
            else if (interval == "TWELVE_HOURS") return true;
            else if (interval == "ONE_DAY") return true;
            else if (interval == "ONE_WEEK") return true;
            else if (interval == "ONE_MONTH") return true;
            else return false;
        },
        showMinutePicker(interval) {
            if (interval == "ONE_HOUR") return true;
            else return false;
        },
        highlightFragments() {
            console.log("highlightFragments called");

            const select = (that, doc) => (item) => {
                const selected = jQuery(".overlay[title='" + item.domQuery + "']", doc);
                if (selected.length == 1) {
                    jQuery(selected).addClass('overlay-selected');
                } else {
                    that.errors.push("Could not find selected fragment \"" + item.name + "\". Page content may have changed recently and you may need to create a new fragment. Learn more in FAQs.");
                }
            }

            var frame = document.getElementById('iframe-render');
            if (frame) {
                const doc = frame.contentDocument? frame.contentDocument: frame.contentWindow.document;
                this._clearHighlightFragments();
                _.each(this.rules, select(this, doc));
            }
        },
        _clearHighlightFragments() {
            console.log("clearHighlightFragments called");
            const frame = document.getElementById('iframe-render');
            if (frame) {
                const doc = frame.contentDocument? frame.contentDocument: frame.contentWindow.document;
                jQuery(".overlay-selected", doc).removeClass('overlay-selected');
            }
        },
        setDefaultRule() {
            this.newRule = {
                "domQuery": "", 
                "name": "", 
                "valueType": "parseText"
            }
        },
        setDefaultSchedule() {
            this.schedule = {
                "interval": "ONE_DAY",
                "name": "New Job",
                "condition": ""
            }
        },
        validateUrlForm: function () {
            this.errors = [];

            if (!/^(http|https):\/\/[^ "]+$/.test(this.url)) {
                this.errors.push("Entered URL is not valid, please ensure URL starts with http or https.");
            }

            if (!this.errors.length) { return true; }
            return false;
        },
        clickRender() {
            this.checkRenderResp = null;
            this.url = this.url.trim();
            if (this.url.startsWith("www.") || this.url.startsWith("WWW.")) {
                this.url = "http://" + this.url;
            }

            if (!this.validateUrlForm()) {
                window.scrollTo(0,0); return;
            }

            this.renderInProgress = true;
            this.submitRender()
                .then(this.pollData)
                .then(this.highlightFragments);
        },
        submitRender () {
            var config = {
                headers: { "Authorization": "bearer " + this.getCookie("token")  }
            };
            var data = {
                "url": this.url.trim()
            }
            return axios.post(AppConfig.BACKEND_ENDPOINT + "/browser/render", data, config).then(result => {
                this.submitRenderResp = result.data.data;
            }).catch(e => {
                console.log(e);
                clearInterval(this.polling);
                if (e.response.status === 403) {
                    this.$emit("authenticated", false);
                    this.$router.replace({ name: "home" });
                }
                this.errors.push(e.response.data.error);
                window.scrollTo(0,0);
            })
        },
        submitRenderFinal () {
            this.submitRender().then(this.highlightFragments);
        },
        checkLastRender() {
            return this._checkRender(false);
        },
        checkFreshRender() {
            return this._checkRender(true);
        },
        _checkRender (fresh) {
            var config = {
                headers: { "Authorization": "bearer " + this.getCookie("token") },
                params: {
                    "url": this.url
                }
            };

            var endpoint = "/browser/fetch-last-render";
            if (fresh) {
                endpoint = "/browser/fetch-fresh-render";
            }

            return axios.get(AppConfig.BACKEND_ENDPOINT + endpoint, config).then(result => {
                this.checkRenderResp = result.data.data;
            }).catch(e => {
                console.log(e);
                clearInterval(this.polling);
                this.renderInProgress = false;
                if (e.response.status === 403) {
                    this.$emit("authenticated", false);
                    this.$router.replace({ name: "home" });
                }
                this.errors.push(e.response.data.error);
            }) 
        },
        checkMode() {
            if (this.$route.params.id) {
                this.scheduleId = this.$route.params.id;
                this.mode = this.$route.name;
            } else {
                this.mode = "new";
            }
        },
        getUrlContent () {
            var config = {
                params: {
                    "ts": new Date().getTime()
                }
            };
            return axios.get(this.checkRenderResp.url, config).then(result => {
                this.checkRenderContent = result.data;
                var that = this;
                var iframe = document.getElementById("iframe-render");
                iframe.contentWindow.document.open();
                iframe.contentWindow.document.write(result.data);
                iframe.contentWindow.document.close();
                iframe.onload = function() {
                    jQuery(function() {
                        jQuery(".overlay", iframe.contentWindow.document).on('click', function () {
                            const domQuery = jQuery(this).attr('title');
                            that.setDefaultRule();
                            that.newRule.domQuery = domQuery;
                            _.each(that.rules, function(item) {
                                if (domQuery == item.domQuery) {
                                    that.newRule.name = item.name;
                                    that.newRule.valueType = item.valueType;
                                }
                            }); 

                            that.$root.$emit('bv::show::modal', 'modal-new-rule');
                        });
                    });
                }
            }).catch(e => {
                console.log(e);
                if (e.response.status === 403) {
                    this.$emit("authenticated", false);
                    this.$router.replace({ name: "home" });
                }
                this.errors.push("Cannot read URL");
                window.scrollTo(0,0);
            }) 

        },
        updateUrl () {
            // document.domain = "localhost";
            document.querySelector("iframe").setAttribute("src", this.checkRenderResp.url);
        },
        validateRuleForm: function() {
            this.errors = [];
            var that = this;
            that.newRule.name = slugify(this.newRule.name, "_"); 

            if (/^\d/.test(this.newRule.name)) {
                this.errors.push("Rule name cannot beging with number character");
            }

            if (this.newRule.name.length < 3) {
                this.errors.push("Rule name must be at least 3 characters long");
            }

            if (!/^[a-zA-Z0-9_-]+$/.test(this.newRule.name)) {
                this.errors.push("Rule name can only contain alphanumeric characters and '_', e.g. 'title_1'");
            }

            if (!this.errors.length) { return true; }
            return false;
        },
        addRule() {
            this.notices = [];
            if (!this.validateRuleForm()) { 
                window.scrollTo(0,0); return; 
            }

            var that = this;
            this.rules = _.filter(that.rules, function(item) { 
                return (item.domQuery != that.newRule.domQuery && item.name != that.newRule.name)
            });

            this.rules.push({
                "name": this.newRule.name,
                "valueType": this.newRule.valueType,
                "domQuery": this.newRule.domQuery
            });
            this.highlightFragments();
        },
        removeRule(idx) {
            this.rules.splice(idx, 1);
            this.highlightFragments();
        },
        loadSchedule (id) {
            var that = this;
            var config = {
                headers: { "Authorization": "bearer " + this.getCookie("token")  }
            };
            function intToStringPad(number) {
                var numberStr = number.toString();
                if (numberStr.length < 2) {
                    return "0" + numberStr;
                } else {
                    return numberStr;
                }
            }

            return axios.get(AppConfig.BACKEND_ENDPOINT + "/schedule/"+id, config).then(result => {
                var data = result.data.data;
                that.rules = data.parseRules;
                that.url = data.url;
                that.schedule.condition = data.evalExpr;
                that.schedule.interval = data.interval;
                that.schedule.name = data.name;

                if (data.startTimeSeconds !== null) {
                    var date = new Date();
                    var hour = Math.floor(data.startTimeSeconds / 3600);
                    var minute = Math.floor(Math.max(data.startTimeSeconds, 3600) % 3600 / 60);
                    var newDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), hour, minute, 0));
                    
                    that.timePicker = {
                        HH: intToStringPad(newDate.getHours()),
                        mm: intToStringPad(newDate.getMinutes())
                    };
                }
            }).catch(e => {
                console.log(e);
                if (e.response.status === 403) {
                    this.$emit("authenticated", false);
                    this.$router.replace({ name: "home" });
                } else if (e.response.status === 404) {
                    this.$router.replace({ name: "jobs" });
                } else {
                    this.$router.replace({ name: "jobs" });
                    alert(e.response.data.error);
                }
            });
        },
        validateScheduleForm: async function() {
            this.errors = [];
            this.notices = [];

            this.schedule.name = this.schedule.name.trim();
            this.schedule.condition = this.schedule.condition.trim();

            await this.validateExpresion().then(result => {
                if (result != "") {
                    this.errors.push("Predicate validation error: " + result);
                }
            });

            if (!this.schedule.name.length) {
                this.errors.push("Job name cannot be empty")
            }

            if (!this.schedule.interval.length) {
                this.errors.push("Interval value has to be set")
            }

            if (!this.errors.length) { return true; }
            return false;
        },
        hhmmToUTCSeconds(hour, minute) {
            var hourInt = parseInt(hour);
            var minuteInt = parseInt(minute);

            if (isNaN(hourInt) && isNaN(minuteInt)) {
                return null;
            }

            if (isNaN(hourInt)) hourInt = 0;
            if (isNaN(minuteInt)) minuteInt = 0;

            var date = new Date();
            var newDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), hourInt, minuteInt, 0);
            return (newDate.getUTCHours() * 3600) + (newDate.getUTCMinutes() * 60);
        },
        async saveSchedule () {
            if (! await this.validateScheduleForm()) {
                window.scrollTo(0,0); return; 
            }

            var config = {
                headers: { "Authorization": "bearer " + this.getCookie("token") }
            };

            var data = {
                "url": this.url,
                "name": this.schedule.name,
                "interval": this.schedule.interval,
                "parseRules": this.rules,
                "evalExpr": this.schedule.condition,
                "notifications": [],
                "notifyOnlyOnSuccess": true,
                "startTimeSeconds": this.hhmmToUTCSeconds(this.timePicker.HH, this.timePicker.mm),
                "enabled": true
            }

            this.overlayOn();
            return axios.post(AppConfig.BACKEND_ENDPOINT + "/schedule", data, config).then(result => {
                this.errors = [];
                this.notices.push("Job has been updated.");
                this.rules = [];
                this.overlayOff();
                this.$router.replace({ name: "jobs" });
            }).catch(e => {
                console.log(e);
                var error = e.response.data.error;
                if (error && error == "MAX_ACTIVE_SCHEDULES_QUOTA_EXCEEDED") {
                    this.errors.push("You have reached maximum number of active schedules for the current plan. Try to disable one of active schedules first and try again.");
                } else if (error && error == "MIN_SCHEDULE_INTERVAL_EXCEEDED") {
                    this.errors.push("Your plan does not allow to set interval to: " + this.schedule.interval);
                } else {
                    this.errors.push("Unknown error");
                }
                if (e.response.status === 403) {
                    this.$emit("authenticated", false);
                    this.$router.replace({ name: "home" });
                }
                window.scrollTo(0,0);
                this.overlayOff();
            })
        },
        saveScheduleFinal() {
            this.saveSchedule().then(this.highlightFragments);
        },
        async updateSchedule () {
            if (! await this.validateScheduleForm()) {
                window.scrollTo(0,0); return; 
            }

            this.errors = [];
            this.notices = [];
            var config = {
                headers: { "Authorization": "bearer " + this.getCookie("token") }
            };
            var data = {
                "id": this.scheduleId,
                "url": this.url,
                "name": this.schedule.name,
                "interval": this.schedule.interval,
                "parseRules": this.rules,
                "evalExpr": this.schedule.condition,
                "startTimeSeconds": this.hhmmToUTCSeconds(this.timePicker.HH, this.timePicker.mm)
            }
            this.overlayOn();
            return axios.patch(AppConfig.BACKEND_ENDPOINT + "/schedule", data, config).then(result => {
                this.errors = [];
                this.notices.push("Job has been updated.");
                window.scrollTo(0,0);
                this.overlayOff();
            }).catch(e => {
                console.log(e);
                var error = e.response.data.error;
                if (error && error == "MAX_ACTIVE_SCHEDULES_QUOTA_EXCEEDED") {
                    this.errors.push("You have reached maximum number of active schedules. Try to disable one of active schedules first.");
                } else if (error && error == "MIN_SCHEDULE_INTERVAL_EXCEEDED") {
                    this.errors.push("Your plan does not allow to set interval to: " + this.schedule.interval);
                } else {
                    this.errors.push("Unknown error");
                }
                if (e.response.status === 403) {
                    this.$emit("authenticated", false);
                    this.$router.replace({ name: "home" });
                }
                window.scrollTo(0,0);
                this.overlayOff();
            }) 
        },
        updateScheduleFinal () {
            this.updateSchedule().then(this.highlightFragments);
        },
        pollData () {
            this.pollCount = 0;
            this.polling = setInterval(() => {
                if (this.pollCount > 15) {
                    clearInterval(this.polling);
                    this.errors.push("Meh, looks like we timed out on this one. Try again?");
                    this.renderInProgress = false;

                } else if (this.checkRenderResp != null && this.checkRenderResp.url != null) {
                    clearInterval(this.polling);
                    this.renderInProgress = false;
                    this.getUrlContent().then(() => {
                        this.highlightFragments();
                    });

                } else {
                    this.checkFreshRender().then(() => {
                        this.pollCount = this.pollCount + 1;
                        console.log(this.pollCount);
                    });
                }
            }, 1500)
        },
        validateExpresion () {
            var that = this;
            var config = {
                headers: { "Authorization": "bearer " + this.getCookie("token") }
            };
            var fragments = {}
            _.each(this.rules, function(el) {
                fragments[el.name] = el.valueType;
            })
            var data = {
                "expression": this.schedule.condition,
                "fragments": fragments
            }
            return axios.post(AppConfig.BACKEND_ENDPOINT + "/browser/testExpression", data, config).then(result => {
                console.log(result.data.data);
                if (result.data.data == "ok") {
                    return "";
                } else if (result.data.data != "ok") {
                    return result.data.data;
                } else if (result.data.error) {
                    return result.data.error;
                }
            }).catch(e => {
                this.errors.push(e.response.data.error);
                window.scrollTo(0,0);
            })
        }
    },
    beforeDestroy () {
        clearInterval(this.polling);
    },
    mounted() {
        this.checkMode();
        console.log(this.$route.name);
        if (this.mode == "edit") {
            this.loadSchedule(this.scheduleId)
                .then(() => this.checkLastRender())
                .then(() => this.getUrlContent())
                .then(() => this.highlightFragments());
        } else {
            this.setDefaultSchedule();
        }
    },
    watch: {
        $route(to, from) {
            console.log(from + " to " + to);
            Object.assign(this.$data, this.$options.data());
            this.setDefaultSchedule();
            clearInterval(this.polling);
        }
    }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
iframe {
    height: 400px;
    border-width: 0px;
    border-color: #c2c2c2;
}
.history-scroll {
    max-height: 300px;
    overflow-y: scroll;
}
</style>